import React, { useEffect, useState, useCallback } from 'react';
import { suppliesAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, formatPhoneInput } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ScannerInput from '@/components/common/ScannerInput';
import { EquipmentPhoto, EquipmentPhotoUploader } from '@/components/supplies/EquipmentPhoto';
import CheckoutTab from '@/components/supplies/CheckoutTab';
import RequestsTab from '@/components/supplies/RequestsTab';
import PackingSlipTemplateDialog from '@/components/supplies/PackingSlipTemplateDialog';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { toast } from 'sonner';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import {
  Package, MapPin, Layers, Plus, Pencil, Trash2, Loader2, Archive,
  Barcode, Boxes, PackageCheck, ArrowLeft, LogOut, Palette, ClipboardList,
} from 'lucide-react';

// KR Enterprise logo, matches the Operations Center header
const KR_LOGO = "https://customer-assets.emergentagent.com/job_35859c3c-f3c9-45c7-a4c9-9ec823ac8280/artifacts/a7ioyzyi_3%20copy.png";

// ── EAN-13 checksum helper (mirrors the backend for early UI feedback) ─
function isValidEan13(code) {
  const s = (code || '').trim();
  if (!/^\d{13}$/.test(s)) return false;
  let total = 0;
  for (let i = 0; i < 12; i += 1) {
    const n = parseInt(s[i], 10);
    total += i % 2 === 0 ? n : n * 3;
  }
  const check = (10 - (total % 10)) % 10;
  return check === parseInt(s[12], 10);
}

const CONDITION_COLORS = {
  new: 'bg-emerald-100 text-emerald-700',
  good: 'bg-sky-100 text-sky-700',
  fair: 'bg-amber-100 text-amber-700',
  damaged: 'bg-rose-100 text-rose-700',
  retired: 'bg-slate-200 text-slate-700',
};
const STATUS_COLORS = {
  available: 'bg-emerald-100 text-emerald-700',
  reserved: 'bg-indigo-100 text-indigo-700',
  on_loan: 'bg-amber-100 text-amber-700',
  maintenance: 'bg-orange-100 text-orange-700',
  retired: 'bg-slate-200 text-slate-700',
};

// ═══════════════════════════════════════════════════════════════════
//   TYPES TAB
// ═══════════════════════════════════════════════════════════════════
function TypesTab() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category: '', default_deposit_amount: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await suppliesAPI.listTypes();
      setTypes(data.items || []);
    } catch (e) {
      toast.error('Failed to load equipment types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', category: '', default_deposit_amount: '' });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      description: row.description || '',
      category: row.category || '',
      default_deposit_amount: row.default_deposit_amount ?? '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        category: form.category.trim() || null,
        default_deposit_amount: form.default_deposit_amount === '' ? null : Number(form.default_deposit_amount),
      };
      if (editing) {
        await suppliesAPI.updateType(editing.type_id, payload);
        toast.success('Type updated');
      } else {
        await suppliesAPI.createType(payload);
        toast.success('Type created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? This cannot be undone.`)) return;
    try {
      await suppliesAPI.deleteType(row.type_id);
      toast.success('Type deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const archive = async (row) => {
    try {
      await suppliesAPI.updateType(row.type_id, { archived: !row.archived });
      toast.success(row.archived ? 'Restored' : 'Archived');
      load();
    } catch (e) {
      toast.error('Archive failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          Logical categories of equipment (e.g. "6ft Table", "KR Banner 8x3"). Every asset belongs to a type.
        </p>
        <Button onClick={openCreate} data-testid="supplies-new-type-btn">
          <Plus size={16} className="mr-1" /> New Type
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">
              <Loader2 className="animate-spin mx-auto mb-2" />
              Loading types...
            </div>
          ) : types.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Layers size={40} className="mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No equipment types yet</p>
              <p className="text-sm">Create your first type to start tracking inventory.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Photo</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Deposit</TableHead>
                  <TableHead>Assets</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {types.map((t) => (
                  <TableRow key={t.type_id} data-testid={`type-row-${t.type_id}`}>
                    <TableCell>
                      <EquipmentPhoto typeId={t.type_id} size={40} />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">{t.name}</div>
                      {t.description && <div className="text-xs text-slate-500">{t.description}</div>}
                      {t.archived && <Badge className="bg-slate-200 text-slate-700 mt-1">Archived</Badge>}
                    </TableCell>
                    <TableCell><span className="capitalize">{t.category || '—'}</span></TableCell>
                    <TableCell>
                      {t.default_deposit_amount != null && t.default_deposit_amount > 0 ? (
                        <span className="font-medium text-slate-800">${Number(t.default_deposit_amount).toFixed(2)}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                          No deposit set
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-emerald-100 text-emerald-700">{t.asset_count_available} available</Badge>
                        <span className="text-xs text-slate-500">of {t.asset_count_total}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-type-${t.type_id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => archive(t)}>
                        <Archive size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(t)} className="text-rose-600">
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Type' : 'New Equipment Type'}</DialogTitle>
            <DialogDescription>Logical category grouping physical assets (e.g. "6ft Table").</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {editing && (
              <div className="pb-2 border-b">
                <EquipmentPhotoUploader typeId={editing.type_id} testId="type-photo-uploader" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="6ft Table" data-testid="type-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="table, banner, signage..." />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional notes about this type" rows={2} />
            </div>
            <div className="space-y-1.5">
              <Label>Default Deposit Amount ($)</Label>
              <Input
                type="number" min="0" step="0.01"
                value={form.default_deposit_amount}
                onChange={(e) => setForm({ ...form, default_deposit_amount: e.target.value })}
                placeholder="e.g. 25.00"
                data-testid="type-form-deposit"
              />
              <p className="text-xs text-slate-500">
                Per-unit deposit. The system caps every request at <strong>$65 total</strong>,
                regardless of how many units are requested.
              </p>
              <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 mt-1">
                <div className="font-medium text-slate-700 mb-1">Suggested defaults</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-slate-600">
                  {[
                    ['Folding table', 25],
                    ['Retractable banner', 30],
                    ['Table cloth', 10],
                    ['Feather flag', 20],
                    ['Tent / canopy 10x10', 65],
                    ['Chair', 5],
                    ['Signage kit', 25],
                    ['Portable speaker', 40],
                  ].map(([label, amount]) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, default_deposit_amount: amount }))}
                      className="text-left hover:text-sky-700 hover:underline"
                    >
                      {label} → ${amount}
                    </button>
                  ))}
                </div>
                <p className="text-slate-500 mt-1">Click any preset to auto-fill.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="type-form-save">
              {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
              {editing ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//   LOCATIONS TAB
// ═══════════════════════════════════════════════════════════════════
function LocationsTab() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = {
    name: '', kind: 'warehouse', address: '', notes: '',
    lat: null, lng: null, place_id: '',
    contact_name: '', contact_phone: '', contact_email: '',
    pickup_hours: '', dropoff_hours: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await suppliesAPI.listLocations();
      setLocations(data.items || []);
    } catch (e) {
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      kind: row.kind || 'warehouse',
      address: row.address || '',
      notes: row.notes || '',
      lat: row.lat ?? null,
      lng: row.lng ?? null,
      place_id: row.place_id || '',
      contact_name: row.contact_name || '',
      contact_phone: row.contact_phone || '',
      contact_email: row.contact_email || '',
      pickup_hours: row.pickup_hours && row.pickup_hours[0]
        ? `${row.pickup_hours[0].open || ''}-${row.pickup_hours[0].close || ''}` : '',
      dropoff_hours: row.dropoff_hours && row.dropoff_hours[0]
        ? `${row.dropoff_hours[0].open || ''}-${row.dropoff_hours[0].close || ''}` : '',
    });
    setDialogOpen(true);
  };

  const parseHours = (str) => {
    if (!str || !str.includes('-')) return [];
    const [openT, closeT] = str.split('-').map((s) => s.trim());
    if (!openT || !closeT) return [];
    // Default to Mon-Fri when nothing else provided.
    return [{ days: [1, 2, 3, 4, 5], open: openT, close: closeT }];
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        kind: form.kind,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
        lat: form.lat, lng: form.lng, place_id: form.place_id || null,
        contact_name: form.contact_name.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        contact_email: form.contact_email.trim() || null,
        pickup_hours: parseHours(form.pickup_hours),
        dropoff_hours: parseHours(form.dropoff_hours),
      };
      if (editing) {
        await suppliesAPI.updateLocation(editing.location_id, payload);
        toast.success('Location updated');
      } else {
        await suppliesAPI.createLocation(payload);
        toast.success('Location created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await suppliesAPI.deleteLocation(row.location_id);
      toast.success('Location deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const isPickupPartner = form.kind === 'pickup_partner';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          Two categories: <strong>Warehouse</strong> (where KR stores assets) and{' '}
          <strong>Pickup Partner</strong> (associated locations where agents pick up / return equipment).
        </p>
        <Button onClick={openCreate} data-testid="supplies-new-location-btn">
          <Plus size={16} className="mr-1" /> New Location
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">
              <Loader2 className="animate-spin mx-auto mb-2" />
              Loading locations...
            </div>
          ) : locations.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <MapPin size={40} className="mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No locations yet</p>
              <p className="text-sm">Add a warehouse or pickup partner to route assets.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.map((l) => (
                  <TableRow key={l.location_id} data-testid={`location-row-${l.location_id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{l.name}</div>
                      {l.contact_name && (
                        <div className="text-xs text-slate-500">
                          {l.contact_name}{l.contact_phone ? ` · ${l.contact_phone}` : ''}
                        </div>
                      )}
                    </TableCell>
                    <TableCell><span className="capitalize">{(l.kind || '').replace('_', ' ')}</span></TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-xs">
                      {l.address || '—'}
                      {l.lat != null && l.lng != null && (
                        <a
                          href={`https://maps.google.com/?q=${l.lat},${l.lng}`}
                          target="_blank" rel="noreferrer"
                          className="ml-2 text-xs text-sky-600 underline"
                        >Map</a>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {l.pickup_hours?.[0]?.open ? `Pickup ${l.pickup_hours[0].open}-${l.pickup_hours[0].close}` : '—'}
                      {l.dropoff_hours?.[0]?.open ? (<><br />{`Return ${l.dropoff_hours[0].open}-${l.dropoff_hours[0].close}`}</>) : null}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(l)} data-testid={`edit-location-${l.location_id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(l)} className="text-rose-600">
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Location' : 'New Location'}</DialogTitle>
            <DialogDescription>
              Warehouses hold KR inventory. Pickup Partners are 3rd-party spots where agents pick up / return equipment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="KR Main Warehouse" data-testid="location-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="location-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="warehouse">Warehouse (KR storage)</SelectItem>
                  <SelectItem value="pickup_partner">Pickup Partner (3rd-party)</SelectItem>
                  <SelectItem value="agent_home">Agent Home</SelectItem>
                  <SelectItem value="event_site">Event Site</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Address</Label>
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                onPlaceSelected={(place) => setForm((f) => ({
                  ...f,
                  address: place.formatted || f.address,
                  lat: place.lat, lng: place.lng, place_id: place.place_id,
                }))}
                testId="location-form-address"
                placeholder="Search a Google Maps address..."
              />
            </div>
            {isPickupPartner && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Contact Name</Label>
                    <Input value={form.contact_name}
                           onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Contact Phone</Label>
                    <Input value={formatPhoneInput(form.contact_phone)}
                           onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Contact Email</Label>
                  <Input type="email" value={form.contact_email}
                         onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Pickup Hours (Mon-Fri)</Label>
                    <Input value={form.pickup_hours}
                           onChange={(e) => setForm({ ...form, pickup_hours: e.target.value })}
                           placeholder="09:00-17:00" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Return Hours (Mon-Fri)</Label>
                    <Input value={form.dropoff_hours}
                           onChange={(e) => setForm({ ...form, dropoff_hours: e.target.value })}
                           placeholder="09:00-17:00" />
                  </div>
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="location-form-save">
              {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
              {editing ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//   ASSETS TAB
// ═══════════════════════════════════════════════════════════════════
function AssetsTab() {
  const [assets, setAssets] = useState([]);
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    type_id: '', ean13: '', label: '', condition: 'new', location_id: '',
    purchase_date: '', purchase_cost: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [scanStatus, setScanStatus] = useState('ready');
  const [scanMessage, setScanMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== 'all') params.type_id = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (search.trim()) params.q = search.trim();
      const [aRes, tRes, lRes] = await Promise.all([
        suppliesAPI.listAssets(params),
        suppliesAPI.listTypes(),
        suppliesAPI.listLocations(),
      ]);
      setAssets(aRes.data.items || []);
      setTypes(tRes.data.items || []);
      setLocations(lRes.data.items || []);
    } catch (e) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      type_id: types[0]?.type_id || '', ean13: '', label: '', condition: 'new',
      location_id: '', purchase_date: '', purchase_cost: '', notes: '',
    });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      type_id: row.type_id || '',
      ean13: row.ean13 || '',
      label: row.label || '',
      condition: row.condition || 'new',
      location_id: row.location_id || '',
      purchase_date: row.purchase_date || '',
      purchase_cost: row.purchase_cost ?? '',
      notes: row.notes || '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.type_id) { toast.error('Type is required'); return; }
    if (!editing && !isValidEan13(form.ean13)) {
      toast.error('EAN-13 must be 13 digits with a valid check digit');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type_id: form.type_id,
        label: form.label.trim() || null,
        condition: form.condition,
        location_id: form.location_id || null,
        purchase_date: form.purchase_date || null,
        purchase_cost: form.purchase_cost === '' ? null : Number(form.purchase_cost),
        notes: form.notes.trim() || null,
      };
      if (editing) {
        await suppliesAPI.updateAsset(editing.asset_id, payload);
        toast.success('Asset updated');
      } else {
        payload.ean13 = form.ean13.trim();
        await suppliesAPI.createAsset(payload);
        toast.success('Asset created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete asset "${row.label || row.ean13}"?`)) return;
    try {
      await suppliesAPI.deleteAsset(row.asset_id);
      toast.success('Asset deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleScan = async (code) => {
    if (!isValidEan13(code)) {
      setScanStatus('error');
      setScanMessage('Invalid EAN-13');
      setTimeout(() => setScanStatus('ready'), 1500);
      return;
    }
    setScanStatus('scanning');
    try {
      const { data } = await suppliesAPI.getAssetByBarcode(code);
      setScanStatus('success');
      setScanMessage(data.label || code);
      openEdit(data);
      setTimeout(() => setScanStatus('ready'), 1500);
    } catch (e) {
      // Unknown barcode → jump into "New Asset" pre-filled
      if (e?.response?.status === 404) {
        setScanStatus('error');
        setScanMessage('Not registered');
        setEditing(null);
        setForm((prev) => ({
          ...prev,
          type_id: types[0]?.type_id || '',
          ean13: code,
          label: '',
          condition: 'new',
          location_id: '',
          purchase_date: '',
          purchase_cost: '',
          notes: '',
        }));
        setDialogOpen(true);
        setTimeout(() => setScanStatus('ready'), 1500);
      } else {
        setScanStatus('error');
        setScanMessage('Lookup failed');
        toast.error('Lookup failed');
        setTimeout(() => setScanStatus('ready'), 1500);
      }
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <ScannerInput
            onScan={handleScan}
            status={scanStatus}
            statusMessage={scanMessage}
            testId="supplies-asset-scanner"
          />
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48" data-testid="filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {types.map((t) => <SelectItem key={t.type_id} value={t.type_id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="reserved">Reserved</SelectItem>
            <SelectItem value="on_loan">On Loan</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Search label or barcode"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64"
          data-testid="filter-search"
        />
        <div className="flex-1" />
        <Button onClick={openCreate} data-testid="supplies-new-asset-btn">
          <Plus size={16} className="mr-1" /> New Asset
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">
              <Loader2 className="animate-spin mx-auto mb-2" />
              Loading assets...
            </div>
          ) : assets.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Boxes size={40} className="mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No assets found</p>
              <p className="text-sm">Register your first physical asset with a unique EAN-13.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Photo</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Barcode (EAN-13)</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assets.map((a) => (
                  <TableRow key={a.asset_id} data-testid={`asset-row-${a.asset_id}`}>
                    <TableCell>
                      <EquipmentPhoto assetId={a.asset_id} typeId={a.type_id} size={40} />
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{a.label || '—'}</TableCell>
                    <TableCell>{a.type_name || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">
                      <span className="inline-flex items-center gap-1"><Barcode size={12} className="text-slate-400" />{a.ean13}</span>
                    </TableCell>
                    <TableCell>{a.location_name || '—'}</TableCell>
                    <TableCell>
                      <Badge className={CONDITION_COLORS[a.condition] || 'bg-slate-100'}>{a.condition}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[a.status] || 'bg-slate-100'}>{(a.status || '').replace('_', ' ')}</Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`edit-asset-${a.asset_id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(a)} className="text-rose-600">
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit Asset — ${editing.label || editing.ean13}` : 'New Asset'}</DialogTitle>
            <DialogDescription>Individual physical unit identified by a unique EAN-13 barcode.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {editing && (
              <div className="pb-2 border-b">
                <EquipmentPhotoUploader assetId={editing.asset_id} testId="asset-photo-uploader" />
                <p className="text-xs text-slate-500 mt-1">
                  If empty, the type&#39;s photo is shown instead.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Type *</Label>
                <Select value={form.type_id} onValueChange={(v) => setForm({ ...form, type_id: v })}>
                  <SelectTrigger data-testid="asset-form-type"><SelectValue placeholder="Choose type" /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => <SelectItem key={t.type_id} value={t.type_id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Condition</Label>
                <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>EAN-13 Barcode *</Label>
              <Input
                value={form.ean13}
                onChange={(e) => setForm({ ...form, ean13: e.target.value.replace(/\D/g, '').slice(0, 13) })}
                disabled={!!editing}
                placeholder="4006381333931"
                className="font-mono tracking-wider"
                data-testid="asset-form-ean13"
              />
              {!editing && form.ean13.length === 13 && !isValidEan13(form.ean13) && (
                <p className="text-xs text-rose-600">Invalid EAN-13 check digit</p>
              )}
              {editing && <p className="text-xs text-slate-500">Barcodes are immutable once assigned.</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Label / Tag</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="KR-TABLE-001" />
            </div>

            <div className="space-y-1.5">
              <Label>Location</Label>
              <Select value={form.location_id || 'none'} onValueChange={(v) => setForm({ ...form, location_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="No location" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Purchase Date</Label>
                <Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Purchase Cost</Label>
                <Input type="number" min="0" step="0.01" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} placeholder="0.00" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="asset-form-save">
              {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
              {editing ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//   PAGE ROOT
// ═══════════════════════════════════════════════════════════════════
export default function SuppliesPage() {
  const { user, logout } = useAuth();
  const isAdmin = isKRRole(user);
  const [summary, setSummary] = useState(null);
  const [templateOpen, setTemplateOpen] = useState(false);

  useEffect(() => {
    if (!user || !isAdmin) return;
    suppliesAPI.summary().then(({ data }) => setSummary(data)).catch(() => {});
  }, [user, isAdmin]);

  if (!user) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="supplies-page">
      {/* Dedicated warehouse-app header — intentionally NOT the CRM MainLayout sidebar */}
      <header className="bg-slate-900 text-white shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4">
          <div className="flex items-center gap-3">
            <img
              src={KR_LOGO}
              alt="KR Enterprise Group"
              className="h-9 w-auto object-contain drop-shadow-md"
              data-testid="supplies-header-logo"
            />
            <div className="hidden sm:block h-8 w-px bg-white/15" />
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">KR Operations</div>
              <div className="text-base font-semibold leading-tight">Event Equipment &amp; Supplies</div>
            </div>
          </div>
          <div className="flex-1" />
          {summary && (
            <div className="hidden md:flex items-center gap-4 text-xs">
              <div className="text-center">
                <div className="text-slate-400 uppercase tracking-wide">Available</div>
                <div className="text-lg font-bold text-emerald-300">{summary.by_status.available}</div>
              </div>
              <div className="text-center">
                <div className="text-slate-400 uppercase tracking-wide">On loan</div>
                <div className="text-lg font-bold text-amber-300">{summary.by_status.on_loan}</div>
              </div>
              <div className="text-center">
                <div className="text-slate-400 uppercase tracking-wide">Total</div>
                <div className="text-lg font-bold">{summary.assets}</div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setTemplateOpen(true)}
              className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white px-2 py-1 rounded-md hover:bg-slate-800"
              data-testid="supplies-open-template"
              title="Packing slip branding"
            >
              <Palette size={14} /> Template
            </button>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white px-2 py-1 rounded-md hover:bg-slate-800"
              data-testid="supplies-back-to-hub"
            >
              <ArrowLeft size={14} /> Back to Hub
            </Link>
            <div className="text-sm text-slate-300 border-l border-slate-700 pl-3 ml-1">
              {user.name || user.email}
            </div>
            <button
              type="button"
              onClick={logout}
              className="text-slate-400 hover:text-white p-1.5 rounded-md hover:bg-slate-800"
              title="Logout"
              data-testid="supplies-logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        <SuppliesTabs />
      </main>

      <PackingSlipTemplateDialog open={templateOpen} onOpenChange={setTemplateOpen} />
    </div>
  );
}

// Controlled Tabs — reads `?tab=` from the URL so RequestsTab can send
// the KR admin straight into a specific tab (e.g. Check-out for a loan
// that's mid-scan). Falls back to `requests`.
function SuppliesTabs() {
  const [params, setParams] = useSearchParams();
  const active = params.get('tab') || 'requests';
  const setActive = (v) => {
    const next = new URLSearchParams(params);
    next.set('tab', v);
    setParams(next, { replace: true });
  };
  return (
    <Tabs value={active} onValueChange={setActive}>
      <TabsList data-testid="supplies-tabs">
        <TabsTrigger value="requests" data-testid="tab-requests"><ClipboardList size={14} className="mr-1.5" />Requests / Orders</TabsTrigger>
        <TabsTrigger value="checkout" data-testid="tab-checkout"><PackageCheck size={14} className="mr-1.5" />Scan &amp; Check-in</TabsTrigger>
        <TabsTrigger value="assets" data-testid="tab-assets"><Boxes size={14} className="mr-1.5" />Assets</TabsTrigger>
        <TabsTrigger value="types" data-testid="tab-types"><Layers size={14} className="mr-1.5" />Types</TabsTrigger>
        <TabsTrigger value="locations" data-testid="tab-locations"><MapPin size={14} className="mr-1.5" />Locations</TabsTrigger>
      </TabsList>
      <TabsContent value="requests" className="pt-4"><RequestsTab /></TabsContent>
      <TabsContent value="checkout" className="pt-4"><CheckoutTab /></TabsContent>
      <TabsContent value="assets" className="pt-4"><AssetsTab /></TabsContent>
      <TabsContent value="types" className="pt-4"><TypesTab /></TabsContent>
      <TabsContent value="locations" className="pt-4"><LocationsTab /></TabsContent>
    </Tabs>
  );
}
